<script lang="ts">
  import { onMount } from "svelte";
  import { push } from "svelte-spa-router";
  import {
    getDeviceDiagnostics,
    mediaProbeRecheck,
    ApiError,
    type AdminDeviceDiagnosticsResponse,
  } from "./api";

  export let label: string;

  type Diagnostics = AdminDeviceDiagnosticsResponse["device"];

  let data: Diagnostics | null = null;
  let loading = true;
  let error: string | null = null;

  let recheckBusy = false;
  let recheckInfo: string | null = null;
  let recheckError: string | null = null;

  async function load() {
    loading = true;
    error = null;
    try {
      const res = await getDeviceDiagnostics(label);
      data = res.device;
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        push("/");
        return;
      }
      error = err instanceof Error ? err.message : "load failed";
    } finally {
      loading = false;
    }
  }

  async function recheck() {
    recheckBusy = true;
    recheckInfo = null;
    recheckError = null;
    try {
      await mediaProbeRecheck(label);
      recheckInfo = "Recheck queued — the device re-probes on its next heartbeat.";
    } catch (err) {
      recheckError = err instanceof Error ? err.message : "recheck failed";
    } finally {
      recheckBusy = false;
    }
  }

  onMount(load);

  const dash = (v: unknown): string =>
    v === null || v === undefined || v === "" ? "—" : String(v);

  const fmtBool = (v: boolean | null | undefined): string =>
    v == null ? "—" : v ? "yes" : "no";

  function fmtTs(ms: number | null | undefined): string {
    return ms == null ? "—" : new Date(ms).toLocaleString();
  }

  function fmtAge(ms: number | null | undefined): string {
    if (ms == null) return "—";
    const s = Math.max(0, Math.floor((Date.now() - ms) / 1000));
    if (s < 60) return `${s}s ago`;
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
  }

  $: caps = data?.media_caps ?? null;
  $: profile = data?.media_profile ?? null;
  $: thermal = caps?.thermal ?? null;
  $: cameras = caps?.cameras ?? [];
  $: roleEntries = profile ? Object.entries(profile.roles) : [];
</script>

<section class="media-diag" data-testid="media-diagnostics">
  <h1>Media diagnostics</h1>

  {#if error}
    <p class="error" data-testid="media-error">{error}</p>
  {/if}

  {#if loading}
    <p data-testid="media-loading">Loading…</p>
  {:else if data}
    {#if !caps}
      <p class="note" data-testid="media-no-caps">
        No media capability probe reported yet.
      </p>
    {/if}

    <div class="grid">
      <fieldset data-testid="media-probe-meta">
        <legend>Probe</legend>
        <dl>
          <dt>Probed</dt>
          <dd data-testid="probe-ts">{fmtTs(caps?.probed_ts ?? data.media_caps_ts)}</dd>
          <dt>Age</dt>
          <dd data-testid="probe-age">{fmtAge(caps?.probed_ts ?? data.media_caps_ts)}</dd>
          <dt>Schema version</dt>
          <dd data-testid="probe-schema">
            {dash(caps?.schema_version ?? data.media_caps_schema_version)}
          </dd>
          <dt>Agent version</dt>
          <dd data-testid="probe-agent">{dash(caps?.agent_version)}</dd>
          <dt>Cache hit</dt>
          <dd data-testid="probe-cache-hit">{fmtBool(caps?.cache_hit)}</dd>
        </dl>
      </fieldset>

      <fieldset data-testid="media-gpu">
        <legend>GPU</legend>
        <dl>
          <dt>Vendor ID</dt>
          <dd data-testid="gpu-vendor">{dash(caps?.gpu?.vendor_id)}</dd>
          <dt>Device ID</dt>
          <dd data-testid="gpu-device">{dash(caps?.gpu?.device_id)}</dd>
          <dt>Driver</dt>
          <dd data-testid="gpu-driver">{dash(caps?.gpu?.driver)}</dd>
          <dt>Render node</dt>
          <dd data-testid="gpu-render-node">{dash(caps?.gpu?.render_node)}</dd>
        </dl>
      </fieldset>

      <fieldset data-testid="media-cpu">
        <legend>CPU</legend>
        <dl>
          <dt>Model</dt>
          <dd data-testid="cpu-model">{dash(caps?.cpu?.model)}</dd>
          <dt>Cores</dt>
          <dd data-testid="cpu-cores">
            {caps?.cpu
              ? `${caps.cpu.physical_cores} physical / ${caps.cpu.logical_cores} logical`
              : "—"}
          </dd>
          <dt>AVX2</dt>
          <dd data-testid="cpu-avx2">{fmtBool(caps?.cpu?.has_avx2)}</dd>
        </dl>
      </fieldset>

      <fieldset data-testid="media-cameras">
        <legend>Cameras</legend>
        {#if cameras.length === 0}
          <p class="note" data-testid="cameras-empty">—</p>
        {:else}
          <table>
            <thead>
              <tr>
                <th>Camera</th>
                <th>Max resolution</th>
                <th>Max fps</th>
                <th>Sustained</th>
              </tr>
            </thead>
            <tbody>
              {#each cameras as cam, i}
                <tr data-testid={`camera-row-${i}`}>
                  <td>{dash(cam.label || cam.path)}</td>
                  <td>{cam.max_width}×{cam.max_height}</td>
                  <td>{dash(cam.max_fps)}</td>
                  <td>{fmtBool(cam.sustained_ok)}</td>
                </tr>
              {/each}
            </tbody>
          </table>
        {/if}
      </fieldset>

      <fieldset data-testid="media-thermal">
        <legend>Thermal</legend>
        <dl>
          <dt>Temperature</dt>
          <dd data-testid="thermal-temp">
            {thermal?.temp_c != null ? `${thermal.temp_c} °C` : "—"}
          </dd>
          <dt>Critical</dt>
          <dd data-testid="thermal-crit">
            {thermal?.temp_crit_c != null ? `${thermal.temp_crit_c} °C` : "—"}
          </dd>
          <dt>Fan</dt>
          <dd data-testid="thermal-fan">
            {thermal?.fan_rpm != null ? `${thermal.fan_rpm} rpm` : "—"}
          </dd>
        </dl>
      </fieldset>

      <fieldset data-testid="media-profile">
        <legend>Resolved profile</legend>
        {#if profile}
          <dl>
            <dt>Rung</dt>
            <dd data-testid="profile-rung">{profile.rung}</dd>
            <dt>Tier</dt>
            <dd data-testid="profile-tier">{profile.tier}</dd>
            <dt>Codec</dt>
            <dd data-testid="profile-codec">{profile.codec}</dd>
            <dt>Performance</dt>
            <dd data-testid="profile-performance">{profile.performance_level}</dd>
          </dl>
          {#if roleEntries.length > 0}
            <ul class="roles" data-testid="profile-roles">
              {#each roleEntries as [path, r], i}
                <li data-testid={`profile-role-${i}`}>
                  <span class="role-path">{path}</span>: {r.role}
                  {r.width}×{r.height}@{r.fps}
                  ({r.content_hint}, {r.degradation_preference})
                </li>
              {/each}
            </ul>
          {/if}
          {#if profile.why.length > 0}
            <ul class="why" data-testid="profile-why">
              {#each profile.why as w}<li>{w}</li>{/each}
            </ul>
          {/if}
        {:else}
          <p class="note" data-testid="profile-none">
            No profile resolved (insufficient probe data).
          </p>
        {/if}
      </fieldset>

      <fieldset data-testid="media-actions">
        <legend>Actions</legend>
        <div class="buttons">
          <button
            type="button"
            on:click={recheck}
            disabled={recheckBusy}
            data-testid="media-recheck"
          >
            {recheckBusy ? "Queuing…" : "Recheck"}
          </button>
          <button type="button" on:click={load} data-testid="media-reload">
            Reload
          </button>
        </div>
        {#if recheckInfo}
          <p class="info" data-testid="media-recheck-info">{recheckInfo}</p>
        {/if}
        {#if recheckError}
          <p class="error" data-testid="media-recheck-error">{recheckError}</p>
        {/if}
      </fieldset>
    </div>
  {/if}
</section>

<style>
  h1 {
    margin: 0 0 1rem;
    font-size: 1.1rem;
  }
  .grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 0.75rem;
  }
  fieldset {
    background: #111827;
    border: 1px solid #1f2937;
    border-radius: 8px;
    padding: 0.75rem 1rem 1rem;
    margin: 0;
  }
  legend {
    padding: 0 0.4rem;
    color: #93c5fd;
    font-weight: 600;
    font-size: 0.85rem;
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }
  dl {
    display: grid;
    grid-template-columns: max-content 1fr;
    gap: 0.25rem 0.75rem;
    margin: 0;
  }
  dt {
    color: #9ca3af;
  }
  dd {
    margin: 0;
    word-break: break-word;
  }
  table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.85rem;
  }
  th,
  td {
    text-align: left;
    padding: 0.25rem 0.4rem;
    border-bottom: 1px solid #1f2937;
  }
  th {
    color: #9ca3af;
    font-weight: 600;
  }
  .roles,
  .why {
    margin: 0.6rem 0 0;
    padding-left: 1.1rem;
    font-size: 0.85rem;
    color: #cbd5e1;
  }
  .why {
    color: #94a3b8;
  }
  .role-path {
    color: #93c5fd;
  }
  .buttons {
    display: flex;
    gap: 0.5rem;
  }
  button {
    padding: 0.5rem 0.8rem;
    background: #2563eb;
    color: #eff6ff;
    border: none;
    border-radius: 6px;
    font-weight: 600;
    cursor: pointer;
  }
  button:disabled {
    opacity: 0.6;
    cursor: default;
  }
  .note {
    color: #9ca3af;
  }
  .info {
    color: #6ee7b7;
    margin: 0.6rem 0 0;
  }
  .error {
    color: #fca5a5;
    margin: 0.6rem 0 0;
  }
</style>
